<template>
  <a-row :gutter="[8, 8]">
    <a-col :span="24">
      <a-row :gutter="8" type="flex" align="middle">
        <a-col>{{ $t("warehouse.belonging_to_warehouse") }}:</a-col>
        <a-col v-if="warehouse">{{ warehouse.warehouseNo }}</a-col>
        <a-col v-else>-</a-col>
      </a-row>
    </a-col>
    <a-col :span="24">
      <a-row :gutter="8" type="flex" align="middle">
        <a-col>{{ $t("warehouse.label_task_name") }}:</a-col>
        <template v-if="isEditName">
          <a-col>
            <a-input
              ref="newTaskNameInputRef"
              v-model:value="newTaskName"
              :maxlength="128"
              style="width: 250px"
              @blur="handleConfirmTaskName"
            ></a-input>
          </a-col>
          <a-col>
            <a-button type="primary" @click="handleConfirmTaskName">{{
              $t("common.confirm")
            }}</a-button>
          </a-col>
        </template>
        <a-col v-else>
          <span
            >{{ formState.taskName }}
            <EditOutlined
              class="text-primary"
              style="cursor: pointer"
              @click="handleShowInput"
          /></span>
        </a-col>
      </a-row>
    </a-col>
    <a-col :span="24" class="mt-3">
      <a-button type="primary" ghost @click="openShipmentModal">{{
        $t("warehouse.select_shipment")
      }}</a-button>
    </a-col>
    <a-col :span="24">
      <a-table
        size="small"
        :columns="columns"
        :data-source="selectedBox"
        :pagination="false"
        :scroll="{ x: 500 }"
        :row-key="(record) => record.whboxId"
      >
        <template #boxNo="{ record }">
          <span class="pr-2">{{ record.boxNo }}</span>
          <a-tag v-if="record.isChangeLabel" color="orange">{{ $t('warehouse.replaced_box_label') }}</a-tag>
        </template>
        <template #shipmentNo>
          {{ shipmentInfo.seShipmentNo }}
        </template>
        <template #shipmentName>
          {{ shipmentInfo.name }}
        </template>
        <template #operation="{ index }">
          <a-button type="ghost" danger @click="handleDelete(index)">{{ $t('common.delete') }}</a-button>
        </template>
      </a-table>
    </a-col>
    <a-col :span="24">
      <a-row type="flex" justify="end" class="mt-5 mb-3">
        <a-col class="mr-3">
          <a-button
            type="primary"
            :loading="confirmLoading"
            @click="handleConfirm"
            >{{ $t("warehouse.create_and_next") }}</a-button
          >
        </a-col>
      </a-row>
    </a-col>
  </a-row>
  <ShipmentListModal ref="shipmentListModalRef" @confirm="handleSetShipment" />
  <BoxListModal v-model:visible="boxListModalState.visible" :shipment="boxListModalState.shipment" @confirm="handleSetBoxList" />
</template>

<script>
import {
  defineComponent,
  reactive,
  toRefs,
  ref,
  nextTick,
  watch,
} from "vue";
import { Button, Col, Input, Row, Table, message, Popover,Tag } from "ant-design-vue";
import CPager from "@/views/components/CPager.vue";
import ShipmentListModal from "./ShipmentListModal.vue";
import BoxListModal from "./BoxListModal.vue";
import { useI18n } from "vue-i18n/index";
import { useRouter } from "vue-router";
import { setFormStateValue } from "@/utils/general.js";
import { createTransitsLabelChangeTask, } from "@/api/modules/transit/labelChange.js";
import { useTab } from "@/hooks/tabs/index";
import { refInputElemFocus } from "@/utils/domoperate";

export default defineComponent({
  name: "stepOne",
  emits: ["next"],
  components: {
    ARow: Row,
    ACol: Col,
    AInput: Input,
    AButton: Button,
    ATable: Table,
    APopover: Popover,
    ATag :Tag,
    CPager,
    ShipmentListModal,
    BoxListModal,
  },
  props: {
    pageState: {
      type: Object,
      default: () => ({}),
    },
    warehouse: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const vueI18n = useI18n({ useScope: "global" });
    const router = useRouter();
    const { delVisitedRoute } = useTab();

    const shipmentListModalRef = ref(null);
    const newTaskNameInputRef = ref(null);

    const state = reactive({
      confirmLoading: false,
      isEditName: false,
      newTaskName: null,
      formState: {
        transitShipmentId: null,
        warehouseId: null,
        taskName: null,
        taskDetails: [],
      },
      shipmentInfo: {
        id: null,
        name: null,
        seShipmentNo: null,
      },
      selectedBox: [],
    });

    const boxListModalState = reactive({
      visible: false,
      shipment: null,
    });

    const columns = [
      {
        title: () => vueI18n.t("warehouse.serial_number"),
        width: 60,
        customRender: ({ text, index }) => {
          return index + 1;
        },
      },
      {
        dataIndex: "canScanedBoxNo",
        width: 150,
        title: () => vueI18n.t("warehouse.scannable_box_no"),
      },
      {
        width: 150,
        title: () => vueI18n.t("warehouse.box_id"),
        slots: {
          customRender: "boxNo",
        },
      },
      {
        width: 150,
        title: () => vueI18n.t("warehouse.se_shipment_no"),
        slots: {
          customRender: "shipmentNo",
        },
      },
      {
        width: 200,
        title: () => vueI18n.t("warehouse.shipment_name"),
        slots: {
          customRender: "shipmentName",
        },
      },
      {
        width: 120,
        title: () => vueI18n.t("common.operation"),
        slots: {
          customRender: "operation",
        },
      }
    ];

    //---------------选择相关的操作 end--------------------

    const handleShowInput = () => {
      state.isEditName = true;
      nextTick(() => {
        refInputElemFocus(newTaskNameInputRef.value.input);
      });
    };

    const handleConfirmTaskName = () => {
      if (!state.newTaskName) {
        state.newTaskName = state.formState.taskName;
      } else {
        state.formState.taskName = state.newTaskName;
      }
      state.isEditName = false;
    };

    const openShipmentModal = () => {
      shipmentListModalRef.value.open(
        state.formState.warehouseId,
        state.shipmentList
      );
    };

    const handleSetShipment = (record) => {
      nextTick(() => {
        boxListModalState.shipment = record;
        boxListModalState.visible = true;
      });
    };

    const handleSetBoxList = (list) => {
      if (Array.isArray(list)) {
        state.selectedBox = list;
        
        state.formState.transitShipmentId = boxListModalState.shipment.id;
        state.shipmentInfo.id = boxListModalState.shipment.id;
        state.shipmentInfo.name = boxListModalState.shipment.name;
        state.shipmentInfo.seShipmentNo = boxListModalState.shipment.seShipmentNo;
      }
    }

    const handleDelete = (index) => {
      state.selectedBox.splice(index, 1);
    };

    const handleConfirm = () => {
      if (!state.formState.taskName) {
        message.error(
          vueI18n.t("common.p0_is_required", [
            vueI18n.t("warehouse.label_task_name"),
          ])
        );
        return;
      }

      if (!state.selectedBox || state.selectedBox.length === 0) {
        message.error(vueI18n.t("warehouse.please_select_box"));
        return;
      }

      const data = Object.assign({}, state.formState);
      let taskDetails = state.selectedBox.map((item) => ({
        whboxId: item.whboxId
      }))
      data.taskDetails = taskDetails;
      state.confirmLoading = true;
      createTransitsLabelChangeTask(data)
        .then(({ result }) => {
          message.success(vueI18n.t("common.succeed"));
          handleNext(result);
        })
        .catch(() => {})
        .finally(() => {
          state.confirmLoading = false;
        });
    };

    const handleNext = (taskId) => {
      delVisitedRoute(router.currentRoute.value);
      router.push("/transit/labelchange/uploadtags/" + taskId);
    };

    watch(
      () => props.pageState,
      (value) => {
        if (value) {
          setFormStateValue(state.formState, value);
          state.newTaskName = value.taskName;
        }
      },
      { deep: true, immediate: true }
    );

    return {
      ...toRefs(state),
      shipmentListModalRef,
      newTaskNameInputRef,
      boxListModalState,
      columns,
      handleShowInput,
      handleConfirmTaskName,
      openShipmentModal,
      handleSetShipment,
      handleSetBoxList,
      handleDelete,
      handleConfirm,
    };
  },
});
</script>

<style scoped></style>
